import {
  createFileRoute,
  Navigate,
  Outlet,
  useLocation,
} from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { PanelLeft } from 'lucide-react'
import apiClient from '@/api/client'
import type { User } from '@/types'
import {
  AdminSidebar,
  adminNavTitleFromPath,
  MOBILE_ADMIN_HEADER_HEIGHT_CLASS,
} from '@/components/admin/AdminSidebar'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import {
  canAccessAdminRoute,
  defaultAdminPath,
  hasSingleAdminScreen,
  isPosOnlyAdminPath,
  isStaffRole,
} from '@/lib/staff-roles'
import { useEdition } from '@/hooks/useEdition'
import { isEmbeddedServerMode } from '@/lib/bhooklyNative'
import { CounterPrintAgent } from '@/components/CounterPrintAgent'
import { AppViewportVar } from '@/components/AppViewportVar'
import { ProductTour } from '@/components/tour/ProductTour'
import { KitchenFlowSetupBanner } from '@/components/admin/KitchenFlowSetupBanner'

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
})

function AdminLayout() {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const location = useLocation()
  // Phone-only drawer threshold: tablets (≥768) get the persistent rail so the
  // 768-1024 band is desktop-class, not a blown-up phone (responsive overhaul).
  const isNarrowViewport = useMediaQuery('(max-width: 767px)')
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const {
    posEnabled,
    isLoading: editionLoading,
    isError: editionError,
  } = useEdition()

  useEffect(() => {
    const storedUser = localStorage.getItem('pos_user')
    const token = localStorage.getItem('pos_token')

    if (storedUser && token) {
      try {
        const parsedUser = JSON.parse(storedUser)
        setUser(parsedUser)
      } catch (error) {
        console.error('Failed to parse stored user:', error)
        localStorage.removeItem('pos_user')
        localStorage.removeItem('pos_token')
      }
    }
    setIsLoading(false)
  }, [])

  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!isNarrowViewport) setMobileNavOpen(false)
  }, [isNarrowViewport])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
          <p className="text-muted-foreground">Loading…</p>
        </div>
      </div>
    )
  }

  if (!apiClient.isAuthenticated() || !user) {
    return <Navigate to="/login" />
  }

  if (!isStaffRole(user.role)) {
    return <Navigate to="/login" replace />
  }

  if (!canAccessAdminRoute(user.role, location.pathname)) {
    return <Navigate to={defaultAdminPath(user.role, posEnabled)} replace />
  }

  // Marketing edition: POS-only paths bounce to that edition's landing page.
  // Never redirect while the edition is still resolving (or failed to load) —
  // redirecting on unresolved settings is how paying POS tenants would get
  // flashed off their own screens (the E-P3 lesson from the online-ordering
  // gate).
  if (!editionLoading && !editionError && !posEnabled && isPosOnlyAdminPath(location.pathname)) {
    return <Navigate to={defaultAdminPath(user.role, false)} replace />
  }

  /** Checkout counter manages its own scroll regions (menu + rail); never page-scroll `<main>`. */
  const isCheckoutCounterRoute = location.pathname === '/admin/counter'
  const embeddedServer = isEmbeddedServerMode()
  // F-FE-N106: the immersive floor mode hides the sidebar, which is the only
  // way to change screens — so it may only apply to someone who has nowhere
  // else to go.
  //
  // It used to key on the shell and the path alone. An admin or manager who
  // opened the server floor on the Bhookly Server tablet therefore lost all
  // navigation and could not get back to the POS, to Reports, or anywhere: the
  // app had to be killed. A waiter is unaffected — `server` reaches exactly one
  // screen, so the chrome would be decoration.
  const hideChrome =
    embeddedServer &&
    hasSingleAdminScreen(user.role) &&
    (location.pathname === '/admin/server' || location.pathname.startsWith('/admin/server/'))

  // Height/offset yield to the desktop update strip when one is showing
  // (F-FE-N47). Both resolve to 0px in a browser, so this is a no-op there.
  // Without the subtraction the shell would be 100dvh BELOW a 40px banner and
  // the counter would start page-scrolling, which breaks its layout.
  return (
    <div className="mt-[var(--desktop-update-banner-h)] flex h-[calc(var(--app-vh,100dvh)_-_var(--desktop-update-banner-h))] overflow-hidden bg-background">
      {/* Keeps --app-vh equal to the REAL window height — some Android tablet
          browsers resolve 100dvh against a stale viewport after rotation,
          which pushed the shell (and the server floor's action rail) below
          the fold with no way to scroll. */}
      <AppViewportVar />
      {/* F-FE-N60. Renders nothing; inert unless this is a desktop terminal with
          the print agent switched on. It sits at the shell level so the counter
          keeps printing tablet-relayed KOTs regardless of which screen is open. */}
      <CounterPrintAgent />
      {/* The product tour: started from Settings or the user menu; renders nothing until then. */}
      <ProductTour />
      {!hideChrome && (
        <div
          className={
            isNarrowViewport ? 'w-0 shrink-0 overflow-visible' : 'min-h-0 shrink-0'
          }
        >
          <AdminSidebar
            user={user}
            isNarrowViewport={isNarrowViewport}
            narrowDrawerOpen={mobileNavOpen}
            onNarrowDrawerOpenChange={setMobileNavOpen}
          />
        </div>
      )}
      <main
        className={cn(
          'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden',
          !isCheckoutCounterRoute && 'md:overflow-y-auto'
        )}
      >
        {!hideChrome && (
          <header
            className={`sticky top-0 z-[60] flex shrink-0 items-center gap-3 border-b border-border bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:hidden ${MOBILE_ADMIN_HEADER_HEIGHT_CLASS} pt-[env(safe-area-inset-top,0px)]`}
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-11 w-11 shrink-0 touch-manipulation"
              aria-label={
                mobileNavOpen ? 'Navigation menu open' : 'Open navigation menu'
              }
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen((o) => !o)}
            >
              <PanelLeft className="h-6 w-6" aria-hidden />
            </Button>
            <span className="min-w-0 flex-1 truncate text-base font-semibold">
              {adminNavTitleFromPath(location.pathname)}
            </span>
          </header>
        )}
        {!hideChrome && <KitchenFlowSetupBanner role={user.role} />}
        <div
          className={cn(
            'min-h-0 flex-1',
            isCheckoutCounterRoute
              ? 'flex flex-col overflow-hidden md:flex md:min-h-0 md:flex-1 md:overflow-hidden'
              : 'overflow-y-auto md:contents'
          )}
        >
          <Outlet />
        </div>
      </main>
    </div>
  )
}
